import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function Home() {
  const [activeCase, setActiveCase] = useState(0);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: '힐링포벳, 분리 불안 CBD',
    description: '홉에서 추출한 CBD로 분리 불안과 스트레스를 케어합니다.',
    keywords: '반려동물 CBD, 홉 CBD, 강아지 CBD, 고양이 CBD, 분리 불안, CBD',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: siteUrl,
          name: '힐링포벳, 분리 불안 CBD',
          description: '홉에서 추출한 CBD로 분리 불안과 스트레스를 케어합니다.',
          inLanguage: 'ko-KR',
          isPartOf: {
            '@id': `${siteUrl}/#website`
          }
        },
        {
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: 'Healing Paw Vet',
          url: siteUrl,
          description: '반려동물 홉 CBD 전문 브랜드'
        },
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: 'Healing Paw Vet',
          inLanguage: 'ko-KR',
          publisher: {
            '@id': `${siteUrl}/#organization`
          }
        }
      ]
    }
  });

  const cases = [
    { icon: 'ri-brain-line', title: '인지기능·치매', desc: '노령견의 방향감각 상실, 배회 증상 완화' },
    { icon: 'ri-pulse-line', title: '발작·뇌전증', desc: '발작 빈도와 강도 감소 연구 데이터' },
    { icon: 'ri-heart-pulse-line', title: '불안·분리불안', desc: '스트레스 호르몬 감소, 안정적인 수면' },
    { icon: 'ri-bone-line', title: '관절·통증', desc: '염증 완화, 움직임 개선' },
    { icon: 'ri-heart-add-line', title: '피부·회복', desc: '아토피, 가려움 완화 및 상처 회복' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-white pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 border border-gray-300 text-sm text-gray-700">
                Non-hemp Phytocannabinoid
              </div>
              <h1 className="text-5xl lg:text-6xl font-light leading-tight text-gray-900">
                대마가 아닌<br />
                홉에서 온 CBD
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                우리 아이를 위한 새로운 선택<br />
                THC 0%, 높은 생체활성, 연구 기반의 안전성
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a 
                  href="https://smartstore.naver.com/hops_cbd/products/11927710702"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-gray-900 text-white hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-center"
                  data-event="cta_contact_hero"
                >
                  상품 구매하기
                </a>
                <button 
                  onClick={() => document.getElementById('waitlist-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 border border-gray-900 text-gray-900 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  data-event="cta_waitlist_hero"
                >
                  출시 알림 신청
                </button>
              </div>
            </div>
            <div className="relative h-[600px]">
              <img 
                src="https://public.readdy.ai/ai/img_res/cb71f28db0620ce26e8b9e1408d9883c.jpg"
                alt="반려동물 케어"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Points */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="w-12 h-12 flex items-center justify-center border border-gray-300">
                <i className="ri-plant-line text-2xl text-gray-900"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900">홉 유래 CBD</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                대마가 아닌 홉(Hop)에서 추출한 식물성 카나비노이드
              </p>
            </div>
            <div className="space-y-4">
              <div className="w-12 h-12 flex items-center justify-center border border-gray-300">
                <i className="ri-shield-check-line text-2xl text-gray-900"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900">THC 0%</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                환각 성분 없이 안전하게 사용 가능한 비정신활성 성분
              </p>
            </div>
            <div className="space-y-4">
              <div className="w-12 h-12 flex items-center justify-center border border-gray-300">
                <i className="ri-line-chart-line text-2xl text-gray-900"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900">높은 생체활성</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                대마 CBD 대비 우수한 흡수율과 체내 활성도
              </p>
            </div>
            <div className="space-y-4">
              <div className="w-12 h-12 flex items-center justify-center border border-gray-300">
                <i className="ri-file-text-line text-2xl text-gray-900"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900">연구 기반</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                다양한 임상 데이터와 안전성 시험 결과 보유
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">홉 CBD vs 대마 CBD</h2>
            <p className="text-gray-600">동일한 효과, 더 안전한 선택</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left py-4 px-6 text-sm font-medium text-gray-900">구분</th>
                  <th className="text-center py-4 px-6 text-sm font-medium text-gray-900">홉 CBD</th>
                  <th className="text-center py-4 px-6 text-sm font-medium text-gray-900">대마 CBD</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">원료 출처</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900">홉(Hop)</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">대마(Hemp)</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">THC 함량</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900">0%</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">&lt;0.3%</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">생체활성도</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900">높음</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">보통</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">법적 규제</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900">자유로움</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">제한적</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-6 text-sm text-gray-700">안전성 데이터</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-900">간독성 없음 확인</td>
                  <td className="py-4 px-6 text-sm text-center text-gray-600">일부 우려 존재</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">어떤 상황에서 고려할 수 있나요</h2>
            <p className="text-gray-600">반려동물의 다양한 건강 문제에 대한 연구 기반 솔루션</p>
          </div>
          <div className="grid md:grid-cols-5 gap-4 mb-12">
            {cases.map((item, idx) => (
              <button
                key={idx}
                onClick={() => setActiveCase(idx)}
                className={`p-6 border transition-all cursor-pointer whitespace-nowrap ${
                  activeCase === idx 
                    ? 'border-gray-900 bg-white' 
                    : 'border-gray-200 bg-white hover:border-gray-400'
                }`}
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <i className={`${item.icon} text-2xl ${activeCase === idx ? 'text-gray-900' : 'text-gray-600'}`}></i>
                </div>
                <div className={`text-sm font-medium ${activeCase === idx ? 'text-gray-900' : 'text-gray-600'}`}>
                  {item.title}
                </div>
              </button>
            ))}
          </div>
          <div className="bg-white border border-gray-200 p-12">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-2xl font-light text-gray-900 mb-6">{cases[activeCase].title}</h3>
              <p className="text-gray-600 leading-relaxed mb-8">{cases[activeCase].desc}</p>
              {activeCase === 0 && (
                <div className="space-y-6">
                  <p className="text-gray-700 leading-relaxed">
                    노령견에서 나타나는 인지기능 저하는 방향감각 상실, 밤낮 혼동, 배회 등의 증상으로 나타납니다. 
                    홉 CBD는 뇌의 엔도카나비노이드 시스템에 작용하여 신경 보호 효과를 나타낼 수 있습니다.
                  </p>
                  <div className="bg-gray-50 p-6 border-l-2 border-gray-900">
                    <p className="text-sm text-gray-700">
                      <strong>연구 결과:</strong> 노령 동물 모델에서 CBD 투여 시 인지 기능 개선 및 신경 염증 감소가 관찰되었습니다.
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    * 반드시 수의사와 상담 후 사용을 결정해야 하며, 개별 반려동물의 상태에 따라 효과는 다를 수 있습니다.
                  </p>
                </div>
              )}
              {activeCase === 1 && (
                <div className="space-y-6">
                  <p className="text-gray-700 leading-relaxed">
                    발작과 뇌전증은 반려동물과 보호자 모두에게 큰 스트레스입니다. 
                    홉 CBD는 신경 안정화 작용을 통해 발작 빈도와 강도를 줄이는 데 도움을 줄 수 있습니다.
                  </p>
                  <div className="bg-gray-50 p-6 border-l-2 border-gray-900">
                    <p className="text-sm text-gray-700">
                      <strong>연구 결과:</strong> 발작 모델 실험에서 홉 CBD가 합성 CBD 및 대마 CBD와 유사하거나 더 나은 항경련 효과를 보였습니다.
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    * 기존 항경련제와의 병용 시 반드시 수의사와 상담이 필요하며, 임의로 약물을 중단하지 마세요.
                  </p>
                </div>
              )}
              {activeCase === 2 && (
                <div className="space-y-6">
                  <p className="text-gray-700 leading-relaxed">
                    분리불안, 소음 공포증, 과도한 짖음 등은 반려동물의 스트레스 호르몬 증가와 관련이 있습니다. 
                    홉 CBD는 세로토닌 수용체에 작용하여 불안을 완화하고 안정적인 수면을 도울 수 있습니다.
                  </p>
                  <div className="bg-gray-50 p-6 border-l-2 border-gray-900">
                    <p className="text-sm text-gray-700">
                      <strong>연구 결과:</strong> 불안 행동 평가에서 CBD 투여군이 대조군 대비 유의미한 스트레스 감소를 보였습니다.
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    * 행동 교정 훈련과 병행할 때 더 효과적이며, 수의사 또는 행동 전문가의 조언을 함께 받으시길 권장합니다.
                  </p>
                </div>
              )}
              {activeCase === 3 && (
                <div className="space-y-6">
                  <p className="text-gray-700 leading-relaxed">
                    관절염, 디스크, 수술 후 통증 등은 반려동물의 삶의 질을 크게 떨어뜨립니다. 
                    홉 CBD는 염증 반응을 조절하고 통증 신호 전달을 완화하여 움직임을 개선할 수 있습니다.
                  </p>
                  <div className="bg-gray-50 p-6 border-l-2 border-gray-900">
                    <p className="text-sm text-gray-700">
                      <strong>연구 결과:</strong> 관절염 모델에서 CBD 투여 시 염증 지표 감소 및 활동성 증가가 확인되었습니다.
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    * 진통제나 항염제와의 병용 시 수의사와 상담이 필요하며, 정기적인 건강 검진을 권장합니다.
                  </p>
                </div>
              )}
              {activeCase === 4 && (
                <div className="space-y-6">
                  <p className="text-gray-700 leading-relaxed">
                    아토피, 알레르기성 피부염, 핥기 행동으로 인한 상처 등은 만성적인 가려움과 염증을 유발합니다. 
                    홉 CBD는 피부 장벽 회복과 염증 완화에 도움을 줄 수 있습니다.
                  </p>
                  <div className="bg-gray-50 p-6 border-l-2 border-gray-900">
                    <p className="text-sm text-gray-700">
                      <strong>연구 결과:</strong> 피부 염증 모델에서 CBD의 항염 및 항산화 효과가 입증되었습니다.
                    </p>
                  </div>
                  <p className="text-sm text-gray-600">
                    * 피부 질환의 원인은 다양하므로 정확한 진단 후 사용하시고, 외용제와 병행 시 수의사와 상담하세요.
                  </p>
                </div>
              )}
              <div className="pt-8">
                <button 
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-3 bg-gray-900 text-white hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  data-event="cta_contact_cases"
                >
                  우리 아이 상담 문의
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-light text-gray-900 mb-4">연구와 데이터로 증명된 안전성</h2>
            <p className="text-gray-600">글로벌 수준의 품질 기준과 임상 데이터</p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center space-y-4">
              <div className="text-5xl font-light text-gray-900">15+</div>
              <div className="text-sm text-gray-600">관련 연구 논문</div>
            </div>
            <div className="text-center space-y-4">
              <div className="text-5xl font-light text-gray-900">0</div>
              <div className="text-sm text-gray-600">간독성 보고 사례</div>
            </div>
            <div className="text-center space-y-4">
              <div className="text-5xl font-light text-gray-900">100%</div>
              <div className="text-sm text-gray-600">THC 무함유</div>
            </div>
          </div>
          <div className="mt-16 text-center">
            <Link 
              to="/science" 
              className="inline-block px-8 py-3 border border-gray-900 text-gray-900 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              연구 데이터 자세히 보기
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=A%20professional%20modern%20veterinary%20clinic%20interior%20with%20clean%20white%20walls%20and%20minimalist%20design%20showing%20a%20caring%20veterinarian%20in%20white%20coat%20consulting%20with%20a%20pet%20owner%20in%20a%20bright%20well-lit%20consultation%20room%20with%20medical%20equipment%20and%20plants%20creating%20a%20trustworthy%20professional%20healthcare%20atmosphere&width=600&height=400&seq=clinic1&orientation=landscape"
                alt="병원 소개"
                className="w-full h-auto"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-light text-gray-900">수의사가 만든 브랜드</h2>
              <p className="text-gray-700 leading-relaxed">
                Kriya Hops CBD는 오랜 임상 경험을 가진 수의사들이 
                반려동물의 삶의 질 개선을 위해 시작한 프로젝트입니다.
              </p>
              <p className="text-gray-700 leading-relaxed">
                기존 대마 CBD의 법적 제약과 안전성 우려를 해결하기 위해 
                홉에서 추출한 CBD에 주목했고, 
                다양한 연구 데이터를 검토한 끝에 
                더 안전하고 효과적인 대안을 찾았습니다.
              </p>
              <p className="text-gray-700 leading-relaxed">
                우리는 과장된 마케팅 대신 
                투명한 데이터와 정직한 소통으로 
                보호자 여러분과 함께하고자 합니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
          <h2 className="text-4xl font-light">우리 아이를 위한 새로운 선택</h2>
          <p className="text-lg text-gray-300">
            출시 전 특별 안내와 상담을 받아보세요
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <button 
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white text-gray-900 hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
              data-event="cta_contact_bottom"
            >
              상담 문의하기
            </button>
            <button 
              onClick={() => document.getElementById('waitlist-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 border border-white text-white hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
              data-event="cta_waitlist_bottom"
            >
              출시 알림 신청
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
